import { createContext, memo, useContext } from 'react';
/**
 * A custom hook to create a type-safe context in a React application.
 *
 * This function creates a context with a specified type `T` and returns
 * a memoized provider component along with a custom hook to consume
 * the context safely.
 *
 * @template T - The type of the context value.
 * @param {string} id - A unique identifier for the context, used in error messages.
 * @returns {{ CtxProvider: React.MemoExoticComponent<React.Provider<T | undefined>>, useCtx: () => T }}
 * - An object containing:
 *   - `CtxProvider`: A memoized context provider component that can be used to wrap components
 *     that need access to the context value.
 *   - `useCtx`: A custom hook that allows consuming the context value. It throws an error if
 *     used outside of a provider, ensuring type safety. The error message includes the identifier
 *     for easier debugging.
 *
 * @example
 * // Usage example
 * import React from 'react';
 * import useCreateContext from './useCreateContext';
 *
 * // Define a type for the context value
 * type UserContextType = {
 *   name: string;
 *   age: number;
 * };
 *
 * // Create a context with the specified type and an identifier
 * const { CtxProvider, useCtx } = useCreateContext<UserContextType>('UserContext');
 *
 * const UserProvider: React.FC<{ value: UserContextType }> = ({ value, children }) => {
 *   return <CtxProvider value={value}>{children}</CtxProvider>;
 * };
 *
 * const UserProfile: React.FC = () => {
 *   const user = useCtx(); // Consumes the context value
 *   return <div>{`Name: ${user.name}, Age: ${user.age}`}</div>;
 * };
 */
const useCreateContext = <T>(id: string, defaultValue?: T) => {
  // Create a context with the type `T | undefined`
  const Context = createContext<T | undefined>(defaultValue);
  // Create a memoized context provider
  const CtxProvider = memo(Context.Provider);
  // Custom hook to consume the context with type safety
  const useCtx = () => {
    const context = useContext(Context);
    if (context === undefined) {
      throw new Error(`${id}: useCtx must be used within a Provider`);
    }
    return context;
  };
  return { CtxProvider, useCtx };
};

export default useCreateContext;
